import { Button, Search, SearchProps } from 'fave-ui';
import { MagnifyingGlass } from 'phosphor-react';
// eslint-disable-next-line local/no-effect
import React, { useEffect, useMemo, useState } from 'react';
import style from '../SearchBarExpandable/style.module.css';
import { useLocation, useNavigate } from 'react-router';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { cn } from '../../../helpers/cn';

const elementIds = {
  searchBar: 'searchBar',
  searchIcon: 'searchIcon',
};

type SearchBarQueryStringProps = Omit<SearchProps, 'onChange'> & {
  alwaysOpen?: boolean;
  alwaysShowBorder?: boolean;
  className?: string;
};

export const SearchBarQueryString: React.FC<SearchBarQueryStringProps> = ({
  alwaysOpen,
  alwaysShowBorder,
  className,
  placeholder = 'Search',
  ...restProps
}: SearchBarQueryStringProps) => {
  const [queryString] = useSearchParams();
  const { ...queryStrings } = Object.fromEntries(queryString);
  const [isOpen, setIsOpen] = useState(!!queryStrings.query);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!queryStrings.query) {
      setIsOpen(false);
    }
  }, [queryStrings.query]);

  const handleSearch = (value: string) => {
    const params = { ...queryStrings, ...(value && { query: value }) };
    navigate(`${location.pathname}?${createSearchParams(params)}`);
  };

  const handleInputClear = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.type === 'click' || e.target.value === '') {
      setIsOpen(false);
      delete queryStrings.query;
      const params = { ...queryStrings };
      navigate(`${location.pathname}?${createSearchParams(params)}`);
    }
  };

  const searchBar = useMemo(
    () =>
      alwaysOpen || isOpen ? (
        <Search
          data-id={elementIds.searchBar}
          defaultValue={queryStrings.query}
          placeholder={placeholder}
          allowClear={true}
          onSearch={handleSearch}
          className={cn(style.isSearch, className, {
            [style.showBorder]: alwaysShowBorder,
          })}
          autoFocus={true}
          onChange={handleInputClear}
          {...restProps}
        />
      ) : (
        <Button
          data-id={elementIds.searchIcon}
          size={'large'}
          shape={'circle'}
          icon={<MagnifyingGlass />}
          onClick={() => setIsOpen(true)}
          className={style.searchButton}
        />
      ),
    [alwaysOpen, isOpen, className, placeholder, alwaysShowBorder, restProps],
  );

  return <div className={style.searchBarExpandable}>{searchBar}</div>;
};
